export const CUSTOMER_PORTAL_SESSION_STORAGE_KEY = "beauti.customer_portal.session";
export const CUSTOMER_PORTAL_SESSION_CHANGED = "beauti:customer-portal-session-changed";
const notifySessionChanged = () => {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(CUSTOMER_PORTAL_SESSION_CHANGED));
};
const DEFAULT_SESSION_TTL_SECONDS = 900;

const storage = () => (typeof window === "undefined" ? null : window.localStorage);

export function expiryMillis(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value < 1_000_000_000_000 ? value * 1000 : value;
  }
  if (typeof value !== "string" || !value.trim()) return NaN;
  const normalized = value.includes("T") ? value : value.replace(" ", "T");
  const withTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized)
    ? normalized
    : `${normalized}Z`;
  return Date.parse(withTimezone);
}

export function readCustomerPortalSession(now = Date.now()) {
  const target = storage();
  if (!target) return null;
  let value;
  try {
    value = JSON.parse(target.getItem(CUSTOMER_PORTAL_SESSION_STORAGE_KEY) || "null");
  } catch (_error) {
    target.removeItem(CUSTOMER_PORTAL_SESSION_STORAGE_KEY);
    return null;
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  if (typeof value.access_token !== "string" || !value.access_token.trim()) {
    target.removeItem(CUSTOMER_PORTAL_SESSION_STORAGE_KEY);
    return null;
  }
  if (value.role_id !== undefined && Number(value.role_id) !== 6) {
    target.removeItem(CUSTOMER_PORTAL_SESSION_STORAGE_KEY);
    return null;
  }
  if (!Number.isFinite(expiryMillis(value.expires_at)) || expiryMillis(value.expires_at) <= now) {
    target.removeItem(CUSTOMER_PORTAL_SESSION_STORAGE_KEY);
    return null;
  }
  return value;
}

export function saveCustomerPortalSession(payload) {
  const target = storage();
  const token = payload && (payload.access_token || payload.token);
  if (!target || typeof token !== "string" || !token.trim()) {
    throw new Error("Customer Portal login did not return a bearer token.");
  }
  const expiresAt = payload.expires_at || new Date(
    Date.now() + Number(payload.expires_in || DEFAULT_SESSION_TTL_SECONDS) * 1000,
  ).toISOString();
  if (!Number.isFinite(expiryMillis(expiresAt)) || expiryMillis(expiresAt) <= Date.now()) {
    throw new Error("Customer Portal login returned an invalid session expiry.");
  }
  const session = {
    ...payload,
    access_token: token.trim(),
    expires_at: expiresAt,
    token_type: payload.token_type || "Bearer",
    role_id: payload.role_id === undefined ? 6 : Number(payload.role_id),
  };
  if (session.role_id !== 6) throw new Error("Customer Portal session is not a client session.");
  target.setItem(CUSTOMER_PORTAL_SESSION_STORAGE_KEY, JSON.stringify(session));
  notifySessionChanged();
  return session;
}

export function clearCustomerPortalSession() {
  const target = storage();
  if (target) {
    target.removeItem(CUSTOMER_PORTAL_SESSION_STORAGE_KEY);
    notifySessionChanged();
  }
}

export function updateCustomerPortalSession(values) {
  const current = readCustomerPortalSession();
  if (!current) return null;
  const updated = { ...current, ...(values || {}) };
  storage().setItem(CUSTOMER_PORTAL_SESSION_STORAGE_KEY, JSON.stringify(updated));
  return updated;
}

export function customerPortalToken() {
  const session = readCustomerPortalSession();
  return session ? session.access_token : null;
}
